import React, { useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  ItemTypeChangeManualEntry,
  ItemTypeChangeManualBatch,
  ItemTypeChangePreviewBinRow,
} from '../../../../types/itemTypeChange';

interface Props {
  binRows: ItemTypeChangePreviewBinRow[];
  newItemType: number;
  entries: ItemTypeChangeManualEntry[];
  onEntriesChange: (entries: ItemTypeChangeManualEntry[]) => void;
}

export const ManualRegistrationTable: React.FC<Props> = ({
  binRows,
  newItemType,
  entries,
  onEntriesChange,
}) => {
  const isSerial = newItemType === 6;
  const isBatch = newItemType === 4 || newItemType === 5;
  const requiresExpiry = newItemType === 4;

  // Sikre at entries matcher binRows ved oppstart
  useEffect(() => {
    if (entries.length !== binRows.length) {
      const next: ItemTypeChangeManualEntry[] = binRows.map((r) => ({
        itemBinsId: r.itemBinsId,
        serialNumbers: isSerial ? Array(Math.round(r.quantity)).fill('') : undefined,
        batches: isBatch ? [{ batchNo: '', quantity: r.quantity, expiryDate: null }] : undefined,
      }));
      onEntriesChange(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [binRows.length]);

  const updateEntry = (itemBinsId: number, update: Partial<ItemTypeChangeManualEntry>) => {
    onEntriesChange(
      entries.map((e) => (e.itemBinsId === itemBinsId ? { ...e, ...update } : e))
    );
  };

  const updateSerial = (itemBinsId: number, index: number, value: string) => {
    const entry = entries.find((e) => e.itemBinsId === itemBinsId);
    if (!entry?.serialNumbers) return;
    const next = [...entry.serialNumbers];
    next[index] = value;
    updateEntry(itemBinsId, { serialNumbers: next });
  };

  const addBatch = (itemBinsId: number) => {
    const entry = entries.find((e) => e.itemBinsId === itemBinsId);
    if (!entry) return;
    const next: ItemTypeChangeManualBatch[] = [
      ...(entry.batches ?? []),
      { batchNo: '', quantity: 0, expiryDate: null },
    ];
    updateEntry(itemBinsId, { batches: next });
  };

  const removeBatch = (itemBinsId: number, idx: number) => {
    const entry = entries.find((e) => e.itemBinsId === itemBinsId);
    if (!entry?.batches) return;
    const next = entry.batches.filter((_, i) => i !== idx);
    updateEntry(itemBinsId, { batches: next });
  };

  const updateBatch = (
    itemBinsId: number,
    idx: number,
    update: Partial<ItemTypeChangeManualBatch>
  ) => {
    const entry = entries.find((e) => e.itemBinsId === itemBinsId);
    if (!entry?.batches) return;
    const next = entry.batches.map((b, i) => (i === idx ? { ...b, ...update } : b));
    updateEntry(itemBinsId, { batches: next });
  };

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <div className="bg-gray-50 p-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800">Manuell registrering per lokasjon</h3>
        <p className="text-xs text-gray-600 mt-0.5">
          {isSerial
            ? 'Registrer ett serienummer per enhet. Alle felt må fylles ut.'
            : 'Registrer en eller flere batcher per lokasjon. Summen må matche mengden.'}
        </p>
      </div>

      <div className="divide-y divide-gray-200">
        {binRows.map((row) => {
          const entry = entries.find((e) => e.itemBinsId === row.itemBinsId);
          return (
            <div key={row.itemBinsId} className="p-3 space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm">
                  <span className="font-mono font-medium">{row.binCode}</span>{' '}
                  <span className="text-gray-500">({row.warehouseID})</span>
                </div>
                <div className="text-xs text-gray-600">
                  Antall: <span className="font-semibold">{row.quantity}</span>
                </div>
              </div>

              {isSerial && entry?.serialNumbers && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {entry.serialNumbers.map((val, i) => (
                    <input
                      key={i}
                      type="text"
                      value={val}
                      onChange={(e) => updateSerial(row.itemBinsId, i, e.target.value)}
                      placeholder={`Serienr. #${i + 1}`}
                      className="px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-red-500"
                    />
                  ))}
                </div>
              )}

              {isBatch && entry?.batches && (
                <div className="space-y-1">
                  {entry.batches.map((batch, idx) => (
                    <div key={idx} className="flex items-center gap-2 flex-wrap">
                      <input
                        type="text"
                        value={batch.batchNo}
                        onChange={(e) =>
                          updateBatch(row.itemBinsId, idx, { batchNo: e.target.value })
                        }
                        placeholder="Batch-nr"
                        className="px-2 py-1 border border-gray-300 rounded text-xs font-mono w-32 focus:ring-1 focus:ring-red-500"
                      />
                      <input
                        type="number"
                        value={batch.quantity}
                        onChange={(e) =>
                          updateBatch(row.itemBinsId, idx, {
                            quantity: parseFloat(e.target.value) || 0,
                          })
                        }
                        placeholder="Antall"
                        className="px-2 py-1 border border-gray-300 rounded text-xs w-24 focus:ring-1 focus:ring-red-500"
                      />
                      {requiresExpiry && (
                        <input
                          type="date"
                          value={batch.expiryDate?.substring(0, 10) ?? ''}
                          onChange={(e) =>
                            updateBatch(row.itemBinsId, idx, {
                              expiryDate: e.target.value ? `${e.target.value}T12:00:00` : null,
                            })
                          }
                          className="px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-red-500"
                        />
                      )}
                      <button
                        onClick={() => removeBatch(row.itemBinsId, idx)}
                        disabled={entry.batches!.length <= 1}
                        className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-30"
                        title="Fjern batch"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addBatch(row.itemBinsId)}
                    className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 mt-1"
                  >
                    <Plus className="h-3 w-3" />
                    Legg til batch
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
